import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';

// Async thunk to create a tour package
export const createTourPackage = createAsyncThunk(
    'packages/createTourPackage',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/tour/tour-packages', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data.tourPackage;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create tour package');
        }
    }
);

// Delete a tour package
export const deleteTourPackage = createAsyncThunk(
    'packages/deleteTourPackage',
    async (id, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(`/tour/delete-tour-packages/${id}`);
            return { id, message: response.data.message };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete tour package');
        }
    }
);

// Update a tour package
export const updateTourPackage = createAsyncThunk(
    'packages/updateTourPackage',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.patch(`/tour/edit-tour-packages/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data.tourPackage;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update tour package');
        }
    }
);

// Async thunk to fetch all tour packages
export const fetchAllPackage = createAsyncThunk(
    'packages/fetchAllPackage',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get('/tour/fetch-all-package');
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch tour packages');
        }
    }
);

// Async thunk to fetch a single tour package by ID
export const fetchTourPackageById = createAsyncThunk(
    'packages/fetchTourPackageById',
    async (id, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/tour/tour-packages/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch tour package');
        }
    }
);

// Async thunk to fetch tour packages by tour type
export const fetchTourPackagesByType = createAsyncThunk(
    'packages/fetchTourPackagesByType',
    async (tourType, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/tour/card-tour-details?tourType=${tourType}`);
            return { tourType, data: response.data };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch tour packages by type');
        }
    }
);

// Async thunk to fetch images from S3
export const fetchImagesFromS3 = createAsyncThunk(
    'packages/fetchImagesFromS3',
    async (tourType, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/tour/s3-images/${tourType}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch tour images');
        }
    }
);

// Async thunk to search tour packages by name
export const searchTourPackagesByName = createAsyncThunk(
    'packages/searchTourPackagesByName',
    async (searchTerm, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/tour/search?title=${encodeURIComponent(searchTerm)}`);
            console.log("search res", response.data)
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to search tour packages');
        }
    }
);

const packagesSlice = createSlice({
    name: 'packages',
    initialState: {
        allPackage: [],
        tourPackages: [],
        domesticPackages: [],
        internationalPackages: [],
        shorttripPackages: [],
        customisedPackages: [],
        activitiesPackages: [],
        searchResults: [],
        currentPackage: null,
        s3Images: [],
        loading: false,
        s3ImagesLoading: false,
        s3ImagesError: null,
        error: null,
        status: 'idle', // idle | loading | succeeded | failed
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearCurrentPackage: (state) => {
            state.currentPackage = null;
        },
        clearSearchResults: (state) => {
            state.searchResults = [];
        },
    },
    extraReducers: (builder) => {
        // Create Tour Package
        builder
            .addCase(createTourPackage.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
                state.error = null;
            })
            .addCase(createTourPackage.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.loading = false;
                state.tourPackages.push(action.payload);
                if (action.payload.tourType === 'Domestic') {
                    state.domesticPackages.push(action.payload);
                } else if (action.payload.tourType === 'International') {
                    state.internationalPackages.push(action.payload);
                }
            })
            .addCase(createTourPackage.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });

        // Delete Tour Package
        builder
            .addCase(deleteTourPackage.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteTourPackage.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.loading = false;
                const id = action.payload.id;
                state.allPackage = state.allPackage.filter(pkg => pkg._id !== id);
                state.domesticPackages = state.domesticPackages.filter(pkg => pkg._id !== id);
                state.internationalPackages = state.internationalPackages.filter(pkg => pkg._id !== id);
                state.shorttripPackages = state.shorttripPackages.filter(pkg => pkg._id !== id);
                state.customisedPackages = state.customisedPackages.filter(pkg => pkg._id !== id);
                state.activitiesPackages = state.activitiesPackages.filter(pkg => pkg._id !== id);
                state.searchResults = state.searchResults.filter(pkg => pkg._id !== id);
            })
            .addCase(deleteTourPackage.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });

        // Update Tour Package
        builder
            .addCase(updateTourPackage.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
                state.error = null;
            })
            .addCase(updateTourPackage.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.loading = false;
                const updatedPackage = action.payload;

                // Replace in allPackage
                const index = state.allPackage.findIndex(pkg => pkg._id === updatedPackage._id);
                if (index !== -1) {
                    state.allPackage[index] = updatedPackage;
                }

                // Replace in type-specific packages
                if (updatedPackage.tourType === 'Domestic') {
                    const i = state.domesticPackages.findIndex(pkg => pkg._id === updatedPackage._id);
                    if (i !== -1) state.domesticPackages[i] = updatedPackage;
                } else if (updatedPackage.tourType === 'International') {
                    const i = state.internationalPackages.findIndex(pkg => pkg._id === updatedPackage._id);
                    if (i !== -1) state.internationalPackages[i] = updatedPackage;
                } else if (updatedPackage.tourType === 'Short') {
                    const i = state.shorttripPackages.findIndex(pkg => pkg._id === updatedPackage._id);
                    if (i !== -1) state.shorttripPackages[i] = updatedPackage;
                } else if (updatedPackage.tourType === 'Customised') {
                    const i = state.customisedPackages.findIndex(pkg => pkg._id === updatedPackage._id);
                    if (i !== -1) state.customisedPackages[i] = updatedPackage;
                } else if (updatedPackage.tourType === 'Activities') {
                    const i = state.activitiesPackages.findIndex(pkg => pkg._id === updatedPackage._id);
                    if (i !== -1) state.activitiesPackages[i] = updatedPackage;
                }

                // Replace in searchResults
                const searchIndex = state.searchResults.findIndex(pkg => pkg._id === updatedPackage._id);
                if (searchIndex !== -1) {
                    state.searchResults[searchIndex] = {
                        _id: updatedPackage._id,
                        title: updatedPackage.title,
                        price: parseFloat(updatedPackage.discountedPrice.replace('INR ', '').replace(/,/g, '')),
                        originalPrice: parseFloat(updatedPackage.originalPrice.replace('INR ', '').replace(/,/g, '')),
                        savings: parseFloat(updatedPackage.originalPrice.replace('INR ', '').replace(/,/g, '')) - parseFloat(updatedPackage.discountedPrice.replace('INR ', '').replace(/,/g, '')),
                        rating: updatedPackage.rating,
                        reviews: updatedPackage.reviewCount,
                        days: updatedPackage.duration,
                        itinerary: updatedPackage.route,
                        images: updatedPackage.images.map(img => img.url),
                    };
                }

                // If it's the currentPackage
                if (state.currentPackage && state.currentPackage._id === updatedPackage._id) {
                    state.currentPackage = updatedPackage;
                }
            })
            .addCase(updateTourPackage.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });

        // Fetch All Tour Packages
        builder
            .addCase(fetchAllPackage.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllPackage.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.loading = false;
                state.allPackage = action.payload;
                state.domesticPackages = action.payload.filter(pkg => pkg.tourType === 'Domestic');
                state.internationalPackages = action.payload.filter(pkg => pkg.tourType === 'International');
                state.shorttripPackages = action.payload.filter(pkg => pkg.tourType === 'Short');
                state.customisedPackages = action.payload.filter(pkg => pkg.tourType === 'Customised');
                state.activitiesPackages = action.payload.filter(pkg => pkg.tourType === 'Activities');
            })
            .addCase(fetchAllPackage.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });

        // Fetch Single Tour Package
        builder
            .addCase(fetchTourPackageById.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTourPackageById.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.loading = false;
                state.currentPackage = action.payload;
            })
            .addCase(fetchTourPackageById.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });

        // Fetch S3 Tour Images by tourType
        builder
            .addCase(fetchImagesFromS3.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchImagesFromS3.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.loading = false;
                state.s3Images = action.payload;
            })
            .addCase(fetchImagesFromS3.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });

        // Fetch Tour Packages by Type
        builder
            .addCase(fetchTourPackagesByType.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTourPackagesByType.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.loading = false;
                if (action.payload.tourType === 'Domestic') {
                    state.domesticPackages = action.payload.data;
                } else if (action.payload.tourType === 'International') {
                    state.internationalPackages = action.payload.data;
                } else if (action.payload.tourType === 'Short') {
                    state.shorttripPackages = action.payload.data;
                } else if (action.payload.tourType === 'Customised') {
                    state.customisedPackages = action.payload.data;
                }
                else if (action.payload.tourType === 'Activities') {
                    state.activitiesPackages = action.payload.data;
                }
            })
            .addCase(fetchTourPackagesByType.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });

        // Search Tour Packages by Name
        builder
            .addCase(searchTourPackagesByName.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
                state.error = null;
            })
            .addCase(searchTourPackagesByName.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.loading = false;
                state.searchResults = action.payload;
            })
            .addCase(searchTourPackagesByName.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// Export actions
export const { clearError, clearCurrentPackage, clearSearchResults } = packagesSlice.actions;

// Export reducer
export default packagesSlice.reducer;